import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

// @mui material components
import Grid from "@mui/material/Grid";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Card from "@mui/material/Card";

// Material Dashboard 2 PRO React components
import MDBox from "../../../components/MDBox";
import MDButton from "../../../components/MDButton";
import MDTypography from "../../../components/MDTypography";

// Material Dashboard 2 PRO React examples
import DashboardLayout from "../../../shared/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../../shared/Navbars/DashboardNavbar";
import Footer from "../../../shared/Footer";

// NewProduct page components
import AuthorInfo from "./components/AuthorInfo";
import UserInfo from "./components/UserInfo"
import authorService from "../../../services/authorService";

// Esquema de validación con Yup
const AuthorSchema = yup.object().shape({
  name: yup.string().required("El nombre es obligatorio").min(3, "Mínimo 3 caracteres"),
  lastName: yup.string().required("Los apellidos son obligatorios").min(3, "Mínimo 3 caracteres"),
  bio: yup.string().required("La biografía es obligatoria"),
  avatar: yup.string().required("La imagen es obligatoria"),
  email: yup.string().required("El email es obligatorio").email("Formato de email inválido"),
  username: yup.string().required("El nombre de usuario es obligatorio").min(3, "Mínimo 3 caracteres"),
  password: yup
    .string()
    .nullable()
    .transform((value) => (value === "" ? null : value))
    .when("$isEdit", (isEdit, schema) => {
      return isEdit
        ? schema
          .min(8, "La nueva contraseña debe tener al menos 8 caracteres")
          .notRequired()
        : schema
          .required("La contraseña es obligatoria")
          .min(8, "La contraseña debe tener al menos 8 caracteres");
    }),
  socialMedia: yup.object(),
});

const steps = ["1. Sobre el autor", "2. Información de usuario"];

function NewAuthor() {
  let navigate = useNavigate();
  const { id } = useParams();
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const isLastStep = activeStep === steps.length - 1;

  const {
    control,
    handleSubmit,
    setValue,
    trigger,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(AuthorSchema),
    mode: "onChange",
    context: { isEdit: !!id },
    defaultValues: {
      name: "",
      lastName: "",
      bio: "",
      avatar: "",
      email: "",
      username: "",
      password: "",
      socialMedia: {},
    },
  });

  useEffect(() => {
    const fetchAuthor = async () => {
      if (!id) return;

      setIsLoading(true);
      try {
        const { data } = await authorService.getById(id);
        const author = data;

        Object.keys(author).forEach((key) => {
          const value = author[key] || "";
          if (key === "user") {
            setValue("username", value.username);
            setValue("avatar", value.avatar);
          } else {
            if (key === "last_name") {
              setValue("lastName", value);
            } else if (key === "social_media") {
              setValue("socialMedia", value);
            } else {
              setValue(key, value);
            }
          }
        });
      } catch (error) {
        console.error("Error cargando autor:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAuthor();
  }, [id, setValue]);

  const handleNext = async () => {
    const fieldsToValidate =
      activeStep === 0
        ? ["name", "lastName", "bio", "avatar"]
        : ["email", "username", "password", "socialMedia"];

    const result = await trigger(fieldsToValidate);

    if (result) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => setActiveStep(activeStep - 1);

  const onSubmit = async (data) => {

    const author = {
      name: data.name,
      last_name: data.lastName,
      bio: data.bio,
      email: data.email,
      social_media: data.socialMedia,
      user: {
        username: data.username,
        password: data.password,
        avatar: data.avatar,
      }
    }
    setIsLoading(true);
    try {
      if (!id) {
        await authorService.create(author)
      } else {
        await authorService.update(id, author)
      }
      navigate("/authors");
    } catch (error) {
      console.error("Error guardando el autor:", error);
      const errorMessage = error.response?.data?.error || "Error al guardar.";
      alert(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={5} mb={9}>
        <Grid container justifyContent="center">
          <Grid item xs={12} lg={8}>
            <MDBox mt={6} mb={8} textAlign="center">
              <MDTypography variant="h3" fontWeight="bold">
                {id ? "Editar" : "Registrar nuevo"} Autor
              </MDTypography>
              <MDTypography variant="h5" color="secondary">
                Información del Autor
              </MDTypography>
            </MDBox>

            <Card>
              <MDBox mt={-3} mb={3} mx={2}>
                <Stepper activeStep={activeStep} alternativeLabel>
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </MDBox>

              <MDBox p={3}>
                <form onSubmit={handleSubmit(onSubmit)}>
                  {activeStep === 0 && (
                    <AuthorInfo control={control} errors={errors} />
                  )}
                  {activeStep === 1 && (
                    <UserInfo control={control} errors={errors} />
                  )}

                  <MDBox mt={4} display="flex" justifyContent="space-between">
                    {activeStep === 0 ? (
                      <MDBox />
                    ) : (
                      <MDButton variant="gradient" color="light" onClick={handleBack}>
                        Atrás
                      </MDButton>
                    )}

                    <MDButton
                      variant="gradient"
                      color="info"
                      onClick={isLastStep ? handleSubmit(onSubmit) : handleNext}
                      disabled={isLoading}
                    >
                      {isLoading ? "Guardando..." : isLastStep ? "Guardar" : "Siguiente"}
                    </MDButton>
                  </MDBox>
                </form>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default NewAuthor;
